import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faMicrophone, faTimes, faVolumeUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { isSoftPhoneActive, useSoftPhone } from '@/contexts/softPhoneContext';

export const DeviceSelect = ({
  label,
  icon,
  devices,
  selectedId,
  onChange,
  disabled
}: {
  label: string;
  icon: IconDefinition;
  devices: MediaDeviceInfo[];
  selectedId: string | null;
  onChange: (id: string) => void;
  disabled: boolean;
}) => (
  <label className="flex flex-col gap-1">
    <span className="flex items-center gap-1.5 text-xs text-gray-400 font-medium uppercase tracking-wide">
      <FontAwesomeIcon icon={icon} className="text-[10px]" />
      {label}
    </span>
    <select
      disabled={disabled}
      value={selectedId ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded bg-gray-700 text-white text-xs py-1.5 px-2 border border-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
      {devices.length === 0 ? (
        <option value="">{disabled ? 'Loading…' : 'Allow mic access to list devices'}</option>
      ) : (
        <>
          <option value="">Default</option>
          {devices.map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || `Device ${d.deviceId.slice(0, 8)}`}
            </option>
          ))}
        </>
      )}
    </select>
  </label>
);

export const AudioDevicePanel = ({
  onClose,
  loading,
  className = ''
}: {
  onClose: () => void;
  loading: boolean;
  className?: string;
}) => {
  const softPhone = useSoftPhone();
  if (!isSoftPhoneActive(softPhone)) return null;

  const {
    availableInputDevices,
    availableOutputDevices,
    inputDeviceId,
    outputDeviceId,
    isOutputSelectionSupported,
    setInputDevice,
    setOutputDevice
  } = softPhone;

  return (
    <div
      className={`bg-gray-900 text-white rounded-xl px-4 py-3 shadow-lg w-64 flex flex-col gap-3 ${className}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-200">Audio Devices</span>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
          aria-label="Close audio settings">
          <FontAwesomeIcon icon={faTimes} className="text-xs" />
        </button>
      </div>

      <DeviceSelect
        label="Microphone"
        icon={faMicrophone}
        devices={availableInputDevices}
        selectedId={inputDeviceId}
        onChange={(id) => void setInputDevice(id)}
        disabled={loading}
      />

      {isOutputSelectionSupported && (
        <DeviceSelect
          label="Speaker"
          icon={faVolumeUp}
          devices={availableOutputDevices}
          selectedId={outputDeviceId}
          onChange={(id) => void setOutputDevice(id)}
          disabled={loading}
        />
      )}
    </div>
  );
};
